import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Header, Form, Popup, Icon, Flag } from 'semantic-ui-react';
import { Segment, Table, SearchTicker, Button } from 'components';
import { BuildOptimize, Storage } from 'actions';
import { getTickerSearchResult } from 'utils/helpers';
import styled from 'styled-components';
import cn from 'classnames';
import { colors } from 'utils/colors';
import Selection from '../../../components/Selection';

class BuildOptimizePortfolio extends Component {
  componentWillMount = () => {
    const { dispatch, match, user_region } = this.props;
    const { id } = match.params;
    dispatch(Storage.setItem('toggle_region', user_region));
    dispatch(Storage.removeItem('pwpa-check-history')); // Clear local storage for include all holdings toggle cache
    dispatch(Storage.removeItem('pwpa-has-exclude-holding'));

    return Number(id) ? dispatch(BuildOptimize.fetchId(id)) : false;
  };

  round = (num) => Math.round(num * 100) / 100;

  labelData = () => {
    const { buildoptimize } = this.props;
    const totalValue = Object.values(buildoptimize.portfolio).reduce(
      (total, value) => (total += Number(value.weight)),
      0
    );

    if (!totalValue || this.round(totalValue) === 100) {
      return false;
    }

    return {
      val: this.round(Math.abs(100 - totalValue)),
      msg: 100 - totalValue > 0 ? 'under' : 'over',
    };
  };

  handleRiskChange = (e, data) => {
    const { dispatch } = this.props;
    const { name, value } = data;
    return dispatch(BuildOptimize.update(name, value));
  };

  remove = (ticker) => {
    const { dispatch } = this.props;

    return dispatch(BuildOptimize.remove(ticker));
  };

  // eslint-disable-next-line consistent-return
  toggleCrypto = (ticker, value) => {
    //  Ticker: '~BTCUSDT' or '~ETHUSDT'
    const { dispatch } = this.props;
    if (value === true) {
      return dispatch(BuildOptimize.add(ticker));
    }
    return this.remove(ticker);
  };

  removeAll = () => this.props.dispatch(BuildOptimize.removeAll());

  isDisabled = () => {
    const { buildoptimize } = this.props;
    const securities = Object.values(buildoptimize.portfolio);
    return securities.length <= 1;
  };

  resetMinWeight = () =>
    this.props.dispatch(BuildOptimize.update('minWeight', 0));

  render() {
    const { buildoptimize, user_region, handler } = this.props;
    const { search, riskMetric, portfolio, isFetching } = buildoptimize;
    const riskMetrics = [
      { key: 'variance', text: 'Total Risk', value: 'variance' },
      { key: 'semivariance', text: 'Downside Risk', value: 'semivariance' },
    ];
    const includeBTC = '~BTCUSDT' in portfolio;
    const includeETH = '~ETHUSDT' in portfolio;

    return (
      <div style={{ height: '100%' }}>
        <Segment
          style={{
            minHeight: '100%',
            textAlign: 'center',
            padding: '2rem 6rem',
          }}
        >
          <Header
            size="medium"
            textAlign="center"
            style={{ marginTop: '36px' }}
          >
            Efficient Frontier Optimizer
            <Header.Subheader
              style={{
                marginTop: '1rem',
                maxWidth: '70ch',
              }}
            >
              Harry Markowitz (1927-2023) was a pioneer in portfolio management
              research and a recipient of the 1990 Nobel Prize in Economics
              Science. His groundbreaking portfolio selection model from the
              1950s emphasized the importance of risk and diversification. In
              this tool, you can use Markowitz's "Efficient Frontier" for
              optimizing your portfolio weights, given a set of securities you
              input. For the risk measure, you can choose between total risk
              (standard deviation) and downside risk (semi-variance).
            </Header.Subheader>
          </Header>
          <div style={{ textAlign: 'left', marginTop: '3rem' }}>
            <Header
              style={{ fontSize: '15.5px', marginBottom: '.3rem' }}
              content="Risk Measure Preference:"
            />
            <Form.Select
              size="small"
              placeholder="Select"
              name="riskMetric"
              options={riskMetrics}
              value={riskMetric}
              onChange={this.handleRiskChange}
              style={{ width: '200px' }}
            />
          </div>
          <div style={{ textAlign: 'left', marginTop: '3rem', width: '73ch' }}>
            <Header
              style={{ fontSize: '15.5px', marginBottom: '.3rem' }}
              content="Add Holdings"
            />
            <Note>
              {user_region === 'CA'
                ? 'Currently, we have data for Canadian and US stocks/ETFs, Canadian mutual and segregated funds, and CAD/USD HISA funds.'
                : 'Note: Currently we only have data for US-listed stocks and ETFs, and US mutual funds.'}
              {user_region === 'CA' ? (
                <Popup
                  trigger={
                    <Icon
                      name="question circle outline"
                      style={{
                        fontSize: '15px',
                        verticalAlign: 'initial',
                        color: 'dimgrey',
                      }}
                    />
                  }
                  content="CAD/USD HISA funds are included in the analysis using the historical rates of a representative bank-owned CAD/USD HISA fund."
                />
              ) : null}{' '}
              Always check the fund code or ticker symbol to ensure you have the
              correct share class. Use US instead of U.S..
            </Note>
            <b />
            <Note style={{ color: colors.blue }}>
              Note: Holdings must have at least 3 years of history.
            </Note>
          </div>

          <SearchTicker
            loading={isFetching}
            results={getTickerSearchResult(search)}
            featureAction={BuildOptimize}
          />
          {Object.keys(portfolio).length > 0 && (
            <Segment
              className="basic compact"
              style={{
                opacity: isFetching ? 0.5 : 1,
              }}
            >
              <Table
                textAlign="center"
                style={{
                  marginBottom: '0rem',
                  borderColor: !!this.labelData() && colors.red,
                }}
              >
                <Table.Header>
                  <Table.Row style={{ position: 'relative' }}>
                    <Popup
                      trigger={
                        <RemoveAll onClick={this.removeAll}>clear</RemoveAll>
                      }
                      wide
                      position="top center"
                      content="Clear all."
                    />

                    <Table.HeaderCell
                      style={{ background: 'rgba(203,232,238,0.3)' }}
                    >
                      Asset
                    </Table.HeaderCell>
                    <Table.HeaderCell
                      style={{ background: 'rgba(203,232,238,0.3)' }}
                    >
                      Exchange
                    </Table.HeaderCell>
                    <Table.HeaderCell
                      style={{ background: 'rgba(203,232,238,0.3)' }}
                    >
                      Class
                    </Table.HeaderCell>
                    <Table.HeaderCell
                      style={{ background: 'rgba(203,232,238,0.3)' }}
                    >
                      Price
                    </Table.HeaderCell>
                    <Table.HeaderCell
                      // colSpan={3}
                      style={{ background: 'rgba(203,232,238,0.3)' }}
                    >
                      Start Date{' '}
                      <Popup
                        trigger={
                          <Icon
                            name="question circle outline"
                            style={{ fontSize: '1rem', verticalAlign: 'top' }}
                          />
                        }
                        position="right center"
                        content="The start date is the month after the inception/IPO date of your holding or April 2007, whichever comes later."
                      />
                    </Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {portfolio &&
                    Object.keys(portfolio).length > 0 &&
                    Object.values(portfolio)
                      .reverse()
                      .map((security) => (
                        <StyledRow
                          negative={
                            security.ticker === '~BTCUSDT' ||
                            security.ticker === '~ETHUSDT'
                              ? !security.unadjusted_closing_price
                              : !security.unadjusted_closing_price.CAD
                          }
                          key={security.ticker}
                          style={{ height: 52, position: 'relative' }}
                        >
                          <i
                            className="close"
                            onClick={() => this.remove(security.ticker)}
                          >
                            +
                          </i>
                          <Table.Cell
                            width={5}
                            style={{ position: 'relative' }}
                          >
                            {security.ticker || 'N/A'}
                            <SecurityName>
                              {security.long_name || 'N/A'}
                            </SecurityName>
                            {security.ticker !== '~BTCUSDT' &&
                              security.ticker !== '~ETHUSDT' &&
                              !security.unadjusted_closing_price.CAD && (
                                <Error>
                                  <Popup
                                    trigger={
                                      <Icon
                                        style={{
                                          transform: 'translate(-20px, -4px)',
                                          position: 'absolute',
                                          display: 'block',
                                          marginTop: '0',
                                        }}
                                        name="exclamation circle"
                                      />
                                    }
                                    position="left center"
                                    content="Unfortunately, this security cannot be added as its price is $0"
                                  />
                                </Error>
                              )}
                          </Table.Cell>
                          <Table.Cell>
                            {security.exchange_symbol || 'N/A'}
                          </Table.Cell>
                          <Table.Cell>
                            {security.global_asset_class || 'N/A'}
                          </Table.Cell>
                          <Table.Cell>
                            $
                            {this.round(
                              security.unadjusted_closing_price[
                                security.default_currency
                              ] || 0
                            )}
                            <Flag
                              name={
                                security.default_currency === 'CAD'
                                  ? 'ca'
                                  : 'us'
                              }
                              style={{ marginLeft: '0.3rem' }}
                            />
                          </Table.Cell>
                          <Table.Cell style={{ width: '170px' }}>
                            {security.start_date || 'N/A'}
                          </Table.Cell>
                        </StyledRow>
                      ))}
                </Table.Body>
              </Table>
              <Segment style={{ margin: '0px', padding: '0px' }}>
                {!!this.labelData() && (
                  <Label>{`${this.labelData().val}% ${
                    this.labelData().msg
                  }`}</Label>
                )}
              </Segment>
            </Segment>
          )}
          <div style={{ textAlign: 'left', marginTop: '2rem' }}>
            <Header
              style={{
                fontSize: '14.72px',
                margin: '0',
              }}
            >
              Add Bitcoin and/or Ethereum
              <Popup
                trigger={
                  <Icon
                    name="question circle outline"
                    style={{
                      fontSize: '15px',
                      verticalAlign: 'initial',
                      color: 'dimgrey',
                    }}
                  />
                }
                position="right center"
                wide
              >
                <Popup.Content>
                  Bitcoin and Ethereum can be added to test hypothetical
                  portfolio ideas. Please note that our analysis is based on
                  historical prices in the coin market, as cryptocurrency ETFs
                  are too new. Our data goes back to October 2014 for Bitcoin
                  and to September 2015 for Ethereum.
                </Popup.Content>
              </Popup>
            </Header>

            <Header.Subheader
              style={{ color: 'rgba(0,0,0,0.5)', fontSize: '0.9rem' }}
            >
              (Optional)
            </Header.Subheader>
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              width: '200px',
            }}
          >
            <Header.Subheader>Bitcoin</Header.Subheader>
            <Selection
              selected={includeBTC}
              changeSelection={() => this.toggleCrypto('~BTCUSDT', !includeBTC)}
            />
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              width: '200px',
            }}
          >
            <Header.Subheader>Ethereum</Header.Subheader>
            <Selection
              selected={includeETH}
              changeSelection={() => this.toggleCrypto('~ETHUSDT', !includeETH)}
            />
          </div>
          <Button
            className={cn('left centered', { disabled: this.isDisabled() })}
            style={{ marginTop: '4rem' }}
            color={colors.darkBlue}
            icon="chart pie"
            onClick={() => {
              handler(Object.values(portfolio));
              handler(buildoptimize.riskMetric);
              this.resetMinWeight();
            }}
          >
            Optimize
          </Button>
        </Segment>
      </div>
    );
  }
}

BuildOptimizePortfolio.propTypes = {
  match: PropTypes.object.isRequired,
  buildoptimize: PropTypes.object,
  dispatch: PropTypes.func,
  user_region: PropTypes.string,
  handler: PropTypes.func,
};

BuildOptimizePortfolio.defaultProps = {
  dispatch: () => false,
  buildoptimize: {},
  user_region: 'CA',
  handler: () => {},
};

export default BuildOptimizePortfolio;

const SecurityName = styled.div`
  font-size: 0.9rem;
  line-height: 1rem;
  color: ${(props) =>
    props.negative ? 'rgba(159,58,56, 0.6)' : 'rgba(0,0,0,0.5)'};
`;

const StyledRow = styled(({ className, children, ...rest }) => (
  <Table.Row className={className} {...rest}>
    {children}
  </Table.Row>
))`
  position: relative !important;

  .close {
    position: absolute;
    display: block;
    margin: -10px;
    margin-top: 20px;
    z-index: 100;
    transform: rotate(45deg);
    text-align: center;
    font-size: 1.7rem;
    font-style: normal;
    border-radius: 18px;
    height: 20px;
    width: 20px;
    line-height: 17px;
    padding: 0 1px;
    background: white;
    border: 1px solid #e4e5e5;
    color: black;
    cursor: pointer;
    transition: background 0.1s ease, border 0.1s ease;
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -khtml-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;

    &:hover {
      background: #fff6f6;
      border: 1px solid #db2828;
    }
  }
`;

const RemoveAll = styled.div`
  position: absolute;
  top: 50%;
  left: -25px;
  transform: translateY(-50%);
  background: white;
  width: 50px;
  font-size: 0.9rem;
  border-radius: 10px;
  line-height: 20px;
  text-align: center;
  border: 1px solid #dededf;
  cursor: pointer;
  transition: border 0.2s ease;

  &:hover {
    border: 1px solid #db2828;
    background: #fff6f6;
  }
`;

const Label = styled.div`
  display: inline-block;
  position: absolute;
  bottom: -10px;
  left: 50%;
  color: white;
  transform: translateX(-50%);
  padding: 1px 20px;
  font-size: 0.9rem;
  background: ${colors.red};
  text-align: center;
  border-radius: 12px;
`;

const Error = styled.div`
  font-size: 1.7rem;
  position: absolute;
  top: 50%;
  left: -2.3rem;
  margin: -0.8rem auto;
  color: ${colors.red};
`;

const Note = styled.div`
  font-size: 1.1rem;
  color: rgba(0, 0, 0, 0.5);
  margin: 0 0 1rem 0;
`;
